"use strict";
var _a, _b;
Object.defineProperty(exports, "__esModule", { value: true });
exports.EvidenceController = void 0;
const tslib_1 = require("tslib");
/**
 * Public evidence upload controller — NO auth required.
 * Customers reach this via the token link sent by the agent.
 */
const common_1 = require("@nestjs/common");
const platform_express_1 = require("@nestjs/platform-express");
const multer_1 = require("multer");
const path_1 = require("path");
const fs_1 = require("fs");
const typeorm_1 = require("@nestjs/typeorm");
const typeorm_2 = require("typeorm");
const ticket_evidence_entity_1 = require("./ticket-evidence.entity");
const service_ticket_entity_1 = require("./service-ticket.entity");
const UPLOAD_DIR = (0, path_1.join)(process.cwd(), 'uploads', 'evidence');
const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
const MAX_FILES = 4;
if (!(0, fs_1.existsSync)(UPLOAD_DIR))
    (0, fs_1.mkdirSync)(UPLOAD_DIR, { recursive: true });
let EvidenceController = class EvidenceController {
    constructor(linkRepo, ticketRepo) {
        this.linkRepo = linkRepo;
        this.ticketRepo = ticketRepo;
    }
    /** Public: get link info (ticket summary + already-uploaded files) */
    async getLinkInfo(token) {
        const link = await this.linkRepo.findOne({
            where: { token },
            relations: ['ticket', 'ticket.customer'],
        });
        if (!link)
            throw new common_1.NotFoundException('Upload link not found or has expired.');
        if (link.expiresAt && link.expiresAt < new Date()) {
            throw new common_1.BadRequestException('This upload link has expired.');
        }
        return {
            token,
            ticketId: link.ticketId,
            ticketNumber: link.ticket.ticketNumber,
            ticketType: link.ticket.ticketType,
            description: link.ticket.description,
            customerName: link.ticket.customer?.name,
            files: link.files ?? [],
            uploadComplete: link.uploadComplete,
            maxFiles: MAX_FILES,
            expiresAt: link.expiresAt,
        };
    }
    /** Public: upload up to 4 image/video files */
    async uploadFiles(token, files) {
        const link = await this.linkRepo.findOne({ where: { token } });
        if (!link)
            throw new common_1.NotFoundException('Upload link not found.');
        if (link.expiresAt && link.expiresAt < new Date())
            throw new common_1.BadRequestException('Link expired.');
        if (link.uploadComplete)
            throw new common_1.BadRequestException('Evidence has already been submitted. New uploads are locked.');
        const existing = link.files ?? [];
        if (existing.length + files.length > MAX_FILES) {
            throw new common_1.BadRequestException(`Maximum ${MAX_FILES} files allowed. You already have ${existing.length}.`);
        }
        const newFiles = files.map(f => ({
            url: `/uploads/evidence/${f.filename}`,
            originalName: f.originalname,
            mimeType: f.mimetype,
            size: f.size,
            type: f.mimetype.startsWith('video/') ? 'video' : 'image',
            uploadedAt: new Date().toISOString(),
        }));
        link.files = [...existing, ...newFiles];
        await this.linkRepo.save(link);
        return { success: true, files: link.files, total: link.files.length };
    }
    /** Public: delete one uploaded file before final submission */
    async deleteFile(token, body) {
        const link = await this.linkRepo.findOne({ where: { token } });
        if (!link)
            throw new common_1.NotFoundException('Upload link not found.');
        if (link.expiresAt && link.expiresAt < new Date())
            throw new common_1.BadRequestException('Link expired.');
        if (link.uploadComplete)
            throw new common_1.BadRequestException('Evidence has already been submitted. Files can no longer be removed.');
        if (!body.url)
            throw new common_1.BadRequestException('File URL is required.');
        const existing = link.files ?? [];
        const file = existing.find((item) => item.url === body.url);
        if (!file)
            throw new common_1.NotFoundException('File not found.');
        const absolutePath = (0, path_1.join)(process.cwd(), file.url.replace(/^\//, ''));
        if ((0, fs_1.existsSync)(absolutePath)) {
            (0, fs_1.unlinkSync)(absolutePath);
        }
        link.files = existing.filter((item) => item.url !== body.url);
        await this.linkRepo.save(link);
        return { success: true, files: link.files, total: link.files.length };
    }
    /** Public: mark upload as done */
    async markComplete(token) {
        const link = await this.linkRepo.findOne({ where: { token } });
        if (!link)
            throw new common_1.NotFoundException('Upload link not found.');
        link.uploadComplete = true;
        await this.linkRepo.save(link);
        return { success: true };
    }
};
exports.EvidenceController = EvidenceController;
tslib_1.__decorate([
    (0, common_1.Get)(':token'),
    tslib_1.__param(0, (0, common_1.Param)('token')),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], EvidenceController.prototype, "getLinkInfo", null);
tslib_1.__decorate([
    (0, common_1.Post)(':token/upload'),
    (0, common_1.UseInterceptors)((0, platform_express_1.FilesInterceptor)('files', MAX_FILES, {
        storage: (0, multer_1.diskStorage)({
            destination: UPLOAD_DIR,
            filename: (_req, file, cb) => {
                const unique = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
                cb(null, `${unique}${(0, path_1.extname)(file.originalname)}`);
            },
        }),
        fileFilter: (_req, file, cb) => {
            if (ALLOWED_MIME.includes(file.mimetype)) {
                cb(null, true);
            }
            else {
                cb(new common_1.BadRequestException(`File type not allowed: ${file.mimetype}. Only images and videos accepted.`), false);
            }
        },
        limits: { fileSize: 100 * 1024 * 1024 }, // 100 MB per file
    })),
    tslib_1.__param(0, (0, common_1.Param)('token')),
    tslib_1.__param(1, (0, common_1.UploadedFiles)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Array]),
    tslib_1.__metadata("design:returntype", Promise)
], EvidenceController.prototype, "uploadFiles", null);
tslib_1.__decorate([
    (0, common_1.Delete)(':token/file'),
    tslib_1.__param(0, (0, common_1.Param)('token')),
    tslib_1.__param(1, (0, common_1.Body)()),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String, Object]),
    tslib_1.__metadata("design:returntype", Promise)
], EvidenceController.prototype, "deleteFile", null);
tslib_1.__decorate([
    (0, common_1.Post)(':token/complete'),
    tslib_1.__param(0, (0, common_1.Param)('token')),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [String]),
    tslib_1.__metadata("design:returntype", Promise)
], EvidenceController.prototype, "markComplete", null);
exports.EvidenceController = EvidenceController = tslib_1.__decorate([
    (0, common_1.Controller)('evidence'),
    tslib_1.__param(0, (0, typeorm_1.InjectRepository)(ticket_evidence_entity_1.TicketEvidenceLink)),
    tslib_1.__param(1, (0, typeorm_1.InjectRepository)(service_ticket_entity_1.ServiceTicket)),
    tslib_1.__metadata("design:paramtypes", [typeof (_a = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _a : Object, typeof (_b = typeof typeorm_2.Repository !== "undefined" && typeorm_2.Repository) === "function" ? _b : Object])
], EvidenceController);
